import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type NotificationSettings, NotificationSettingsSchema } from '@vision360/contracts';
import { Check, CircleOff, Send } from 'lucide-react';
import { type FormEvent, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/errors';
import { usePlatform } from '@/lib/platform/context';
import type { NotificationPermissionState } from '@/lib/platform/types';
import { cn } from '@/lib/utils';
import {
  useNotificationSettings,
  useSaveNotificationSettings,
  useSendTestNotification,
} from './api';

/**
 * Settings → Notifications (step 32, TechSpec §12.3): quiet hours and their timezone, whether
 * the server can send Telegram (and a test message), and the native notification permission.
 */

export const PERMISSION_KEY = ['platform', 'notification-permission'] as const;

const CHIP = 'inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium';

/** An IANA zone the browser knows; the API reads the window in the same zone. */
export function isTimeZone(zone: string): boolean {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('fr-FR', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

function Switch({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-10 shrink-0 items-center rounded-full border transition-colors duration-150 pointer-coarse:h-7 pointer-coarse:w-12',
        checked ? 'border-primary bg-primary' : 'border-input bg-muted',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-4 w-4 rounded-full bg-background shadow-sm transition-transform duration-150 ease-out',
          checked ? 'translate-x-[1.125rem] pointer-coarse:translate-x-6' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function QuietHoursForm({ settings }: { settings: NotificationSettings }) {
  const { t } = useTranslation('notifications');
  const [draft, setDraft] = useState(settings);
  const [submitted, setSubmitted] = useState(false);
  const save = useSaveNotificationSettings();
  const ids = { switch: useId(), start: useId(), end: useId(), zone: useId(), hint: useId() };
  const { quietHours: quiet } = draft;

  const timezone = draft.timezone.trim();
  const parsed = NotificationSettingsSchema.safeParse({ ...draft, timezone });
  const badTime = (field: 'start' | 'end') =>
    submitted && parsed.error?.issues.some((i) => i.path[1] === field) === true;
  // The API answers 400 VALIDATION on `timezone` for a zone its runtime does not know.
  const serverRefused = save.error instanceof ApiError && save.error.code === 'VALIDATION';
  const badZone = (submitted && !isTimeZone(timezone)) || serverRefused;
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  const setQuiet = (patch: Partial<NotificationSettings['quietHours']>) =>
    setDraft((d) => ({ ...d, quietHours: { ...d.quietHours, ...patch } }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!parsed.success || !isTimeZone(timezone)) return;
    save.mutate(parsed.data);
  };

  return (
    <form className="space-y-4" noValidate onSubmit={submit}>
      <div className="space-y-1">
        <h3 className="font-medium">{t('settings.quiet.title')}</h3>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('settings.quiet.description')}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Switch
          id={ids.switch}
          checked={quiet.enabled}
          onChange={(enabled) => setQuiet({ enabled })}
        />
        <Label htmlFor={ids.switch}>{t('settings.quiet.enabled')}</Label>
      </div>
      <div
        className={cn('space-y-3 transition-opacity duration-150', !quiet.enabled && 'opacity-60')}
      >
        <div className="flex flex-wrap items-end gap-3">
          {(['start', 'end'] as const).map((field) => (
            <div key={field} className="space-y-1.5">
              <Label htmlFor={ids[field]}>{t(`settings.quiet.${field}`)}</Label>
              <Input
                id={ids[field]}
                type="time"
                step={60}
                className="w-32 font-mono tabular-nums"
                value={quiet[field]}
                disabled={!quiet.enabled}
                aria-invalid={badTime(field)}
                onChange={(e) => setQuiet({ [field]: e.target.value })}
              />
            </div>
          ))}
          <div className="min-w-48 flex-1 space-y-1.5">
            <Label htmlFor={ids.zone}>{t('settings.quiet.timezone')}</Label>
            <Input
              id={ids.zone}
              className="max-w-xs font-mono"
              value={draft.timezone}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={badZone}
              aria-describedby={ids.hint}
              onChange={(e) => {
                save.reset();
                setDraft((d) => ({ ...d, timezone: e.target.value }));
              }}
            />
          </div>
        </div>
        <p id={ids.hint} className="text-xs text-muted-foreground">
          {quiet.start > quiet.end
            ? t('settings.quiet.windowOvernight', { start: quiet.start, end: quiet.end })
            : t('settings.quiet.window', { start: quiet.start, end: quiet.end })}{' '}
          {t('settings.quiet.timezoneHint')}
        </p>
      </div>
      <div aria-live="polite" className="space-y-1 text-sm text-destructive">
        {(badTime('start') || badTime('end')) && <p>{t('settings.quiet.invalidTime')}</p>}
        {badZone && <p>{t('settings.quiet.invalidTimezone')}</p>}
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending && <Spinner />}
          {save.isPending ? t('settings.saving') : t('settings.save')}
        </Button>
        <output aria-live="polite" className="text-sm">
          {save.isSuccess && !dirty && (
            <span className="inline-flex animate-rise items-center gap-1 text-muted-foreground">
              <Check aria-hidden="true" className="h-4 w-4 text-primary" />
              {t('settings.saved')}
            </span>
          )}
          {save.isError && !serverRefused && (
            <span className="text-destructive">{t('settings.saveFailed')}</span>
          )}
        </output>
      </div>
    </form>
  );
}

function TelegramBlock({ configured }: { configured: boolean }) {
  const { t } = useTranslation('notifications');
  const test = useSendTestNotification();
  const status = test.error instanceof ApiError ? test.error.status : undefined;
  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium">{t('settings.telegram.title')}</h3>
        {configured ? (
          <span className={cn(CHIP, 'bg-primary/12 text-primary')}>
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
            {t('settings.telegram.configured')}
          </span>
        ) : (
          <span className={cn(CHIP, 'bg-muted text-muted-foreground')}>
            <CircleOff aria-hidden="true" className="h-3.5 w-3.5" />
            {t('settings.telegram.notConfigured')}
          </span>
        )}
      </div>
      {!configured && (
        <p className="max-w-prose text-sm text-muted-foreground">
          {t('settings.telegram.notConfiguredHint')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          disabled={!configured || test.isPending}
          onClick={() => test.mutate({ channel: 'telegram' })}
        >
          {test.isPending ? <Spinner /> : <Send aria-hidden="true" className="h-4 w-4" />}
          {test.isPending ? t('settings.telegram.testing') : t('settings.telegram.test')}
        </Button>
        <output aria-live="polite" className="text-sm">
          {test.isSuccess && (
            <span className="text-muted-foreground">{t('settings.telegram.testSent')}</span>
          )}
          {test.isError && (
            <span className="text-destructive">
              {status === 409
                ? t('settings.telegram.testNotConfigured')
                : status === 502
                  ? t('settings.telegram.testRejected')
                  : t('settings.telegram.testFailed')}
            </span>
          )}
        </output>
      </div>
    </div>
  );
}

const PERMISSION_CHIP: Record<NotificationPermissionState, string> = {
  granted: 'bg-primary/12 text-primary',
  denied: 'bg-red-500/15 text-red-700 dark:text-red-400',
  default: 'bg-muted text-muted-foreground',
};

function NativeBlock() {
  const { t } = useTranslation('notifications');
  const { notificationPermission: read, requestNotificationPermission: request } = usePlatform();
  const queryClient = useQueryClient();
  const permission = useQuery({
    queryKey: PERMISSION_KEY,
    queryFn: () => (read ? read() : Promise.resolve(null)),
    enabled: read !== undefined,
  });
  const ask = useMutation({
    mutationFn: () => (request ? request() : Promise.resolve(null)),
    onSuccess: (state) => queryClient.setQueryData(PERMISSION_KEY, state),
  });

  let body: ReactNode;
  if (!read || !request) {
    body = <p className="text-sm text-muted-foreground">{t('settings.native.unsupported')}</p>;
  } else if (permission.isPending) {
    body = (
      <output aria-live="polite" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('settings.native.loading')}
      </output>
    );
  } else if (permission.isError || !permission.data) {
    body = <p className="text-sm text-destructive">{t('settings.native.error')}</p>;
  } else {
    const state = permission.data;
    body = (
      <div className="space-y-3">
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className={cn(CHIP, PERMISSION_CHIP[state])}>{t(`settings.native.${state}`)}</span>
        </p>
        {state === 'denied' && (
          <p className="max-w-prose text-sm text-muted-foreground">
            {t('settings.native.deniedHint')}
          </p>
        )}
        {state === 'default' && (
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" disabled={ask.isPending} onClick={() => ask.mutate()}>
              {ask.isPending && <Spinner />}
              {ask.isPending ? t('settings.native.requesting') : t('settings.native.request')}
            </Button>
            <output aria-live="polite" className="text-sm text-destructive">
              {ask.isError && t('settings.native.error')}
            </output>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="space-y-1">
        <h3 className="font-medium">{t('settings.native.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('settings.native.description')}</p>
      </div>
      {body}
    </div>
  );
}

export function NotificationSettingsSection() {
  const { t } = useTranslation('notifications');
  const query = useNotificationSettings();
  return (
    <section aria-labelledby="settings-notifications" className="space-y-4">
      <div>
        <h2 id="settings-notifications" className="text-lg font-semibold">
          {t('settings.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('settings.description')}</p>
      </div>
      {query.isPending ? (
        <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
          <Spinner />
          {t('settings.loading')}
        </output>
      ) : query.isError ? (
        <Alert variant="destructive" role="alert">
          {t('settings.error')}
        </Alert>
      ) : (
        <div className="animate-rise space-y-4">
          <QuietHoursForm settings={query.data.settings} />
          <TelegramBlock configured={query.data.telegram.configured} />
        </div>
      )}
      <NativeBlock />
    </section>
  );
}
